import { ColDef } from '@ag-grid-community/core';
import { Component } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { BaseListEditFrmComponent, BaseService } from '@core';
import {
  Account,
  ActiveStatusEnum,
  ProximityAreaEnum,
  ProximityRight,
  ProximityRightEnum,
  ProximityRole,
} from '@models';
import { UtilFns } from '@utils';
import { AccountDbQueryService } from '../services/account-db-query.service';
import { AccountDbSaveService } from '../services/account-db-save.service';
import { AccountActivationService } from '../services/account-activation.service';

@Component({
    selector: 'prox-role-list',
    templateUrl: 'account-role-list.component.html',
    standalone: false
})
export class AccountRoleListComponent extends BaseListEditFrmComponent<ProximityRole> {
  accountId!: string;
  account!: Account;
  rights!: ProximityRight[];
  area = ProximityAreaEnum.Accounts;

  constructor(
    baseService: BaseService,
    route: ActivatedRoute,
    private activationService: AccountActivationService,
    override dbSaveService: AccountDbSaveService,
    override dbQueryService: AccountDbQueryService
  ) {
    super(baseService, route, dbSaveService, dbQueryService);
  }

  override statusMessage() {
    if (!this.hasRight(ProximityRightEnum.CanManageAccountAdministration)) {
      return 'Not authorized';
    }
    let msg = '';
    if (this.account?.activeStatusId == ActiveStatusEnum.Active) {
      return 'Account is Active and Not Editable.  To edit, set Account status to Hold.';
    } else if (this.account?.activeStatusId == ActiveStatusEnum.Hold) {
      msg = 'Account is On-Hold and is Editable';
    } else {
      return 'Account is ' + this.account?.activeStatus.name + ' and is Not Editable';
    }
    return msg;
  }

  override async updateFromParams(params: object): Promise<void> {
    this.accountId = params['accountId'];
    UtilFns.assertNonEmptyString(this.accountId, 'accountId');
    const [rights, roles, account] = await Promise.all([
      this.dbQueryService.uow.getAllOrQuery(ProximityRight),
      this.dbQueryService.getAccountRolesByAccountId(this.accountId),
      this.dbQueryService.getAccountById(this.accountId),
    ]);
    this.rights = rights;
    this.account = account;

    await this.initialize(ProximityRole, 'Proximity Role', roles);
    this.setTitle('Proximity Roles');
    this.isPageReady = true;
  }

  isReadOnly() {
    return (
      !this.hasRight(ProximityRightEnum.CanManageAccountAdministration) ||
      this.getWorkingStatus(this.account).isActiveReadOnly
    );
  }


  // Role creation/deletion is intentionally disabled (see Add-button removal). These
  // implementations exist to satisfy BaseListEditFrmComponent's abstract contract and
  // are never reached because no UI path invokes Add or Delete on this grid.
  override async createEntity(): Promise<ProximityRole | undefined> {
    return undefined;
  }
  override async checkIfInUse(_entity: ProximityRole): Promise<boolean> {
    return true;
  }

  override async createDefaultEntity(entity: ProximityRole): Promise<ProximityRole | undefined> {
    return undefined;
  }

  override getSortValue(e: ProximityRole) {
    return e.name;
  }

  override getAltKeyValue(e: ProximityRole) {
    return e.name;
  }

  override getColDefs() {
    const columnDefs: ColDef[] = [
      { headerName: 'Name', field: 'name', editable: false, filter: 'agTextColumnFilter' },
      { headerName: 'Description', field: 'description', editable: false, filter: 'agTextColumnFilter' },
      { headerName: 'Type', valueGetter: (p) => this.getTypeLabel(p.data), editable: false, filter: 'agTextColumnFilter' },
    ];
    return columnDefs;
  }

  canEdit(_proximityRole: ProximityRole) {
    return false;
  }

  private getTypeLabel(proximityRole: ProximityRole) {
    return proximityRole && proximityRole.accountId == this.accountId ? this.account.name : 'Global';
  }
}
